// Inline tenant logo for brands that ship a mark rather than a raster banner.
//
// The historic header logo is an <img> resolved from tenant config with a
// BUILD-TIME REACT_APP_LOGO fallback — one value baked into an image that now
// serves every tenant, so it cannot differ per host. Brands declaring
// `logo.kind === "svg"` render here instead, entirely from runtime config.
//
// Inline SVG rather than an asset: it inherits the accent, stays crisp at any
// size, needs no extra network request, and cannot 404 the way a per-tenant
// asset path can.

import { getBrand } from "../config/product";

const MARK_CSS = `
@keyframes bmRev{
  0%{transform:rotate(-132deg)}   2%{transform:rotate(-132deg)}
  10%{transform:rotate(27deg)}   14%{transform:rotate(-9deg)}
  17%{transform:rotate(13deg)}   20%{transform:rotate(-4deg)}
  23%{transform:rotate(3deg)}    26%{transform:rotate(0)}
  88%{transform:rotate(0)}      100%{transform:rotate(-132deg)}
}
.bm-needle{transform-origin:20px 20px;animation:bmRev 4.6s cubic-bezier(.33,.9,.32,1) infinite}
.g2-rotor{transform-origin:20px 20px}
@media (prefers-reduced-motion:reduce){.bm-needle{animation:none}}`;

interface Props {
  /** Height of the mark in px. The wordmark scales from it. */
  size?: number;
  /** Wordmark colour. Defaults to white for the dark app header. */
  color?: string;
}

/**
 * Tenant brand SVG marks:
 * - Aquipe: a tachometer — diagnostics, calibration & alignment.
 * - G2 Aviation: 3-blade helicopter rotor head & airworthiness dial.
 */
const BrandMark: React.FC<Props> = ({ size = 34, color = "#fff" }) => {
  const brand = getBrand();
  if (brand.logo.kind !== "svg") return null;

  const renderMarkGraphic = () => {
    if (brand.logo.kind === "svg" && brand.logo.mark === "g2") {
      return (
        <svg width={size} height={size} viewBox="0 0 40 40" role="img" aria-label={brand.wordmark}>
          <circle cx="20" cy="20" r="18.5" fill="#2B2E33" />
          <path
            d="M7 20 a 13 13 0 1 1 26 0"
            fill="none"
            stroke={brand.accent}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <g className="g2-rotor">
            <path d="M20 20 L20 6" stroke="#FAF8F6" strokeWidth="2.8" strokeLinecap="round" />
            <path d="M20 20 L32.1 27" stroke="#FAF8F6" strokeWidth="2.8" strokeLinecap="round" />
            <path d="M20 20 L7.9 27" stroke="#FAF8F6" strokeWidth="2.8" strokeLinecap="round" />
          </g>
          <circle cx="20" cy="20" r="3.2" fill={brand.accent} />
          <circle cx="20" cy="20" r="1.6" fill="#FAF8F6" />
        </svg>
      );
    }

    // Default SVG mark (aquipe tachometer)
    return (
      <svg width={size} height={size} viewBox="0 0 40 40" role="img" aria-label={brand.wordmark}>
        <circle cx="20" cy="20" r="18.5" fill="#2B2E33" />
        <path
          d="M9.6 27.4a13 13 0 1 1 20.8 0"
          fill="none"
          stroke={brand.accent}
          strokeWidth="3.4"
          strokeLinecap="round"
        />
        <path
          className="bm-needle"
          d="M20 20 L27.2 13.6"
          stroke="#FAF8F6"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx="20" cy="20" r="2.9" fill="#FAF8F6" />
      </svg>
    );
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: size * 0.34 }}>
      <style>{MARK_CSS}</style>
      {renderMarkGraphic()}
      {brand.wordmark && (
        <span
          style={{
            fontFamily: '"Roboto Condensed",Roboto,"Arial Narrow",sans-serif',
            fontWeight: 900,
            fontSize: size * 0.78,
            letterSpacing: ".045em",
            textTransform: "uppercase",
            color,
            lineHeight: 1,
          }}
        >
          {brand.wordmark}
          <span style={{ color: brand.accent }}>.</span>
        </span>
      )}
    </span>
  );
};

export default BrandMark;
